/**
 * Analytics Export Utilities
 *
 * Provides functions to export survey analytics to PDF, PowerPoint, and Excel formats.
 * All exports include SurveyTip branding and survey identification.
 */

import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import pptxgen from 'pptxgenjs'
import * as XLSX from 'xlsx'

// Brand constants
const BRAND = {
  name: 'SurveyTip',
  primaryColor: '#0D9488',
  secondaryColor: '#0EA5E9',
  primaryColorRGB: { r: 13, g: 148, b: 136 },
  secondaryColorRGB: { r: 14, g: 165, b: 233 },
  primaryColorHex: '0D9488',
  secondaryColorHex: '0EA5E9',
}

/**
 * Format date for display in exports
 */
function formatDate(date = new Date()) {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format datetime for filenames (safe characters)
 */
function formatDateForFilename(date = new Date()) {
  return date.toISOString().split('T')[0]
}

/**
 * Sanitize survey title for use in filename
 */
function sanitizeFilename(title) {
  return title
    .replace(/[^a-z0-9\s-]/gi, '')
    .replace(/\s+/g, '_')
    .substring(0, 50)
}

/**
 * Get location string from response
 */
function getLocation(response) {
  const geo =
    response.geolocation ||
    response.metadata?.geolocation ||
    response.client_info?.geolocation ||
    response.meta?.geolocation
  if (!geo) return ''
  const parts = [geo.city, geo.region, geo.country || geo.country_name].filter(Boolean)
  return parts.join(', ')
}

// =============================================================================
// Helpers – render arbitrary text (including non-Latin) as an image via html2canvas
// =============================================================================

/**
 * Render text into a small canvas image so non-Latin scripts (Tamil, Hindi, etc.)
 * display correctly inside jsPDF which only ships Latin fonts.
 *
 * @param {string} text - The text to render
 * @param {Object} opts - Style options
 * @returns {Promise<{imgData: string, width: number, height: number}>}
 */
async function textToImage(
  text,
  { fontSize = 24, fontWeight = 'bold', color = '#323232', maxWidth = 700 } = {}
) {
  const div = document.createElement('div')
  Object.assign(div.style, {
    position: 'fixed',
    left: '-9999px',
    top: '0',
    background: 'transparent',
    padding: '4px 0',
    fontSize: `${fontSize}px`,
    fontWeight,
    color,
    fontFamily: "'Noto Sans', 'Arial Unicode MS', 'Segoe UI', sans-serif",
    maxWidth: `${maxWidth}px`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: '1.35',
  })
  div.textContent = text
  document.body.appendChild(div)

  try {
    const canvas = await html2canvas(div, {
      scale: 2,
      backgroundColor: null, // transparent
      logging: false,
    })
    const imgData = canvas.toDataURL('image/png')
    return { imgData, width: canvas.width, height: canvas.height }
  } finally {
    document.body.removeChild(div)
  }
}

/**
 * Returns true if the text contains characters outside the Basic Latin + Latin-1 Supplement ranges.
 */
function hasNonLatinChars(text) {
  // eslint-disable-next-line no-control-regex
  return /[^\u0000-\u024F]/.test(text)
}

// =============================================================================
// PDF Export
// =============================================================================

/**
 * Export analytics to PDF — professional quality.
 *
 * Layout:
 *   Page 1  — Branded cover (title rendered as image for Unicode safety)
 *   Page 2+ — Two charts per page, stacked vertically
 *
 * @param {Object} survey - Survey data
 * @param {Object} analytics - Analytics summary
 * @param {Array} responses - Response data array
 * @param {HTMLElement} chartsContainer - DOM element containing charts
 * @returns {Promise<void>}
 */
export async function exportToPDF(survey, analytics, responses, chartsContainer) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = pdf.internal.pageSize.getWidth() // ~210
  const pageHeight = pdf.internal.pageSize.getHeight() // ~297
  const margin = 20
  const contentWidth = pageWidth - margin * 2

  const title = survey?.title || 'Survey Analytics'
  const totalResponses = analytics?.total_responses || responses.length
  const completeResponses = analytics?.complete_responses || responses.length
  const completionRate =
    totalResponses > 0 ? Math.round((completeResponses / totalResponses) * 100) : 0

  let pageNum = 1

  // ---- helper: add branded page footer ----
  const addFooter = () => {
    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(160, 160, 160)
    pdf.text(`Powered by ${BRAND.name}`, margin, pageHeight - 12)
    pdf.text(`Page ${pageNum}`, pageWidth - margin - 15, pageHeight - 12)
  }

  // ---- helper: add branded thin header bar on chart pages ----
  const addHeaderBar = async () => {
    pdf.setFillColor(BRAND.primaryColorRGB.r, BRAND.primaryColorRGB.g, BRAND.primaryColorRGB.b)
    pdf.rect(0, 0, pageWidth, 14, 'F')
    pdf.setTextColor(255, 255, 255)
    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'bold')
    pdf.text(BRAND.name, margin, 9.5)

    // Title in header — use image if non-Latin, otherwise plain text
    if (hasNonLatinChars(title)) {
      try {
        const img = await textToImage(title, {
          fontSize: 12,
          fontWeight: 'normal',
          color: '#ffffff',
          maxWidth: 500,
        })
        const hMm = 8
        const wMm = (img.width / img.height) * hMm
        pdf.addImage(img.imgData, 'PNG', pageWidth - margin - wMm, 3, wMm, hMm)
      } catch {
        /* fallback: skip title in header */
      }
    } else {
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(9)
      const tw = pdf.getTextWidth(title)
      pdf.text(title, pageWidth - margin - tw, 9.5)
    }
  }

  // =========================================================================
  // Cover Page
  // =========================================================================

  // Header gradient bar
  pdf.setFillColor(BRAND.primaryColorRGB.r, BRAND.primaryColorRGB.g, BRAND.primaryColorRGB.b)
  pdf.rect(0, 0, pageWidth, 40, 'F')

  // Brand name (always Latin)
  pdf.setTextColor(255, 255, 255)
  pdf.setFontSize(28)
  pdf.setFont('helvetica', 'bold')
  pdf.text(BRAND.name, margin, 28)

  // Survey title — render via image for Unicode support
  if (hasNonLatinChars(title)) {
    try {
      const titleImg = await textToImage(title, {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#323232',
        maxWidth: 650,
      })
      const maxW = contentWidth
      const imgW = Math.min(maxW, (titleImg.width / 2) * 0.264583) // px@2x → mm
      const imgH = (titleImg.height / titleImg.width) * imgW
      pdf.addImage(titleImg.imgData, 'PNG', margin, 55, imgW, imgH)
    } catch {
      pdf.setTextColor(50, 50, 50)
      pdf.setFontSize(20)
      pdf.text(title, margin, 70)
    }
  } else {
    pdf.setTextColor(50, 50, 50)
    pdf.setFontSize(24)
    pdf.setFont('helvetica', 'bold')
    pdf.text(title, margin, 70, { maxWidth: contentWidth })
  }

  // Subtitle
  pdf.setFontSize(14)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(100, 100, 100)
  pdf.text('Analytics Report', margin, 90)

  // Date
  pdf.setFontSize(12)
  pdf.text(`Generated on ${formatDate()}`, margin, 103)

  // Summary stats box
  const statsY = 125
  pdf.setFillColor(250, 249, 245)
  pdf.roundedRect(margin, statsY, contentWidth, 50, 3, 3, 'F')

  const statsColWidth = contentWidth / 3

  // Total Responses
  pdf.setTextColor(80, 80, 80)
  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.text('Total Responses', margin + 10, statsY + 15)
  pdf.setFontSize(24)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(BRAND.primaryColorRGB.r, BRAND.primaryColorRGB.g, BRAND.primaryColorRGB.b)
  pdf.text(String(totalResponses), margin + 10, statsY + 35)

  // Complete
  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(80, 80, 80)
  pdf.text('Complete', margin + statsColWidth + 10, statsY + 15)
  pdf.setFontSize(24)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(BRAND.primaryColorRGB.r, BRAND.primaryColorRGB.g, BRAND.primaryColorRGB.b)
  pdf.text(String(completeResponses), margin + statsColWidth + 10, statsY + 35)

  // Completion Rate
  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(80, 80, 80)
  pdf.text('Completion Rate', margin + statsColWidth * 2 + 10, statsY + 15)
  pdf.setFontSize(24)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(BRAND.primaryColorRGB.r, BRAND.primaryColorRGB.g, BRAND.primaryColorRGB.b)
  pdf.text(`${completionRate}%`, margin + statsColWidth * 2 + 10, statsY + 35)

  addFooter()

  // =========================================================================
  // Chart Pages — 2 charts per page, aspect-ratio preserved
  // =========================================================================

  if (chartsContainer) {
    const chartCards = Array.from(chartsContainer.querySelectorAll('.chart-card'))

    // Extract chart data: title + direct canvas image (pixel-perfect)
    const chartData = []
    for (const card of chartCards) {
      const header = card.querySelector('.chart-header')
      const chartTitle = header?.textContent || 'Chart'
      const canvasEl = card.querySelector('canvas')

      let imgData = null
      let aspectRatio = 16 / 9 // sensible fallback

      if (canvasEl && canvasEl.width > 0 && canvasEl.height > 0) {
        // Use Chart.js canvas directly — crisp, no html2canvas artifacts
        imgData = canvasEl.toDataURL('image/png')
        aspectRatio = canvasEl.width / canvasEl.height
      } else {
        // Fallback: capture the full card with html2canvas
        try {
          const captured = await html2canvas(card, {
            scale: 1.5,
            backgroundColor: '#ffffff',
            logging: false,
          })
          imgData = captured.toDataURL('image/jpeg', 0.85)
          aspectRatio = captured.width / captured.height
        } catch (err) {
          console.error('Failed to capture chart:', err)
        }
      }

      chartData.push({ title: chartTitle, imgData, aspectRatio })
    }

    // Layout constants
    const titleH = 10 // mm reserved for chart title text
    const chartSlotH = 108 // mm available height per chart image
    const blockH = titleH + chartSlotH
    const blockGap = 8
    const firstBlockY = 20 // mm below header bar

    for (let i = 0; i < chartData.length; i += 2) {
      pdf.addPage()
      pageNum++
      await addHeaderBar()

      for (let j = 0; j < 2 && i + j < chartData.length; j++) {
        const chart = chartData[i + j]
        const blockY = firstBlockY + j * (blockH + blockGap)

        // ── Chart title ──
        if (hasNonLatinChars(chart.title)) {
          try {
            const img = await textToImage(chart.title, {
              fontSize: 14,
              fontWeight: 'bold',
              color: '#2d3748',
              maxWidth: 600,
            })
            const hMm = 7
            const wMm = Math.min((img.width / img.height) * hMm, contentWidth)
            pdf.addImage(img.imgData, 'PNG', margin, blockY, wMm, hMm)
          } catch {
            pdf.setTextColor(45, 55, 72)
            pdf.setFontSize(12)
            pdf.setFont('helvetica', 'bold')
            pdf.text(chart.title, margin, blockY + 6, { maxWidth: contentWidth })
          }
        } else {
          pdf.setTextColor(45, 55, 72)
          pdf.setFontSize(12)
          pdf.setFont('helvetica', 'bold')
          pdf.text(chart.title, margin, blockY + 6, { maxWidth: contentWidth })
        }

        // ── Chart image — fit within slot preserving aspect ratio ──
        const imgY = blockY + titleH

        if (chart.imgData) {
          const availW = contentWidth
          const availH = chartSlotH
          const ar = chart.aspectRatio

          let drawW, drawH
          if (ar >= availW / availH) {
            // Image is wider than slot — constrain by width
            drawW = availW
            drawH = availW / ar
          } else {
            // Image is taller than slot — constrain by height
            drawH = availH
            drawW = availH * ar
          }

          // Center horizontally
          const drawX = margin + (availW - drawW) / 2
          const fmt = chart.imgData.startsWith('data:image/png') ? 'PNG' : 'JPEG'
          pdf.addImage(chart.imgData, fmt, drawX, imgY, drawW, drawH)
        } else {
          pdf.setTextColor(160, 160, 160)
          pdf.setFontSize(11)
          pdf.setFont('helvetica', 'normal')
          pdf.text('Chart could not be rendered', margin, imgY + 20)
        }
      }

      addFooter()
    }
  }

  // Save
  const filename = `${sanitizeFilename(title)}_Analytics_${formatDateForFilename()}.pdf`
  pdf.save(filename)
}

// =============================================================================
// PowerPoint Export
// =============================================================================

/**
 * Export analytics to PowerPoint
 *
 * @param {Object} survey - Survey data
 * @param {Object} analytics - Analytics summary
 * @param {Array} responses - Response data array
 * @param {HTMLElement} chartsContainer - DOM element containing charts
 * @returns {Promise<void>}
 */
export async function exportToPPT(survey, analytics, responses, chartsContainer) {
  const pptx = new pptxgen()

  // Unicode-safe font with fallbacks
  const FONT = 'Noto Sans'

  // Set presentation properties
  pptx.author = BRAND.name
  pptx.title = survey?.title || 'Survey Analytics'
  pptx.subject = 'Analytics Report'
  pptx.company = BRAND.name

  // Define polished master slide
  pptx.defineSlideMaster({
    title: 'SURVEYTIP_MASTER',
    background: { color: 'FFFFFF' },
    objects: [
      // Thin top accent line
      { rect: { x: 0, y: 0, w: '100%', h: 0.06, fill: { color: BRAND.primaryColorHex } } },
      // Thin bottom accent line
      { rect: { x: 0, y: 5.44, w: '100%', h: 0.06, fill: { color: BRAND.primaryColorHex } } },
      // Footer left
      {
        text: {
          text: `Powered by ${BRAND.name}`,
          options: { x: 0.5, y: 5.1, w: 3, h: 0.3, fontSize: 8, color: 'AAAAAA', fontFace: FONT },
        },
      },
      // Footer right — date
      {
        text: {
          text: formatDate(),
          options: {
            x: 7,
            y: 5.1,
            w: 2.5,
            h: 0.3,
            fontSize: 8,
            color: 'AAAAAA',
            fontFace: FONT,
            align: 'right',
          },
        },
      },
    ],
  })

  const title = survey?.title || 'Survey Analytics'
  const titleIsNonLatin = hasNonLatinChars(title)
  const totalResponses = analytics?.total_responses || responses.length
  const completeResponses = analytics?.complete_responses || responses.length
  const completionRate =
    totalResponses > 0 ? Math.round((completeResponses / totalResponses) * 100) : 0

  // Pre-render title as image for non-Latin support
  let titleImageData = null
  if (titleIsNonLatin) {
    try {
      const img = await textToImage(title, {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#FFFFFF',
        maxWidth: 800,
      })
      titleImageData = img
    } catch {
      /* fallback to text */
    }
  }

  // -------------------------------------------------------------------------
  // Title Slide
  // -------------------------------------------------------------------------

  const titleSlide = pptx.addSlide()

  // Large header block
  titleSlide.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 0,
    w: '100%',
    h: 2.5,
    fill: { color: BRAND.primaryColorHex },
  })

  // Accent separator under header
  titleSlide.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 2.5,
    w: '100%',
    h: 0.08,
    fill: { color: BRAND.secondaryColorHex },
  })

  // Brand name — small, top-left
  titleSlide.addText(BRAND.name, {
    x: 0.7,
    y: 0.35,
    w: 8.6,
    h: 0.4,
    fontSize: 14,
    fontFace: FONT,
    color: 'FFFFFF',
  })

  // "Analytics Report" — main heading in header
  titleSlide.addText('Analytics Report', {
    x: 0.7,
    y: 1.0,
    w: 8.6,
    h: 1,
    fontSize: 34,
    fontFace: FONT,
    bold: true,
    color: 'FFFFFF',
  })

  // Survey title — below header block
  titleSlide.addText(title, {
    x: 0.7,
    y: 2.9,
    w: 8.6,
    h: 1,
    fontSize: 26,
    fontFace: FONT,
    bold: true,
    color: '333333',
    valign: 'top',
  })

  if (titleImageData) {
    const maxW = 8.6
    const imgW = Math.min(maxW, titleImageData.width / 2 / 96)
    const imgH = (titleImageData.height / titleImageData.width) * imgW
    titleSlide.addImage({
      data: titleImageData.imgData,
      x: 0.7,
      y: 2.9,
      w: imgW,
      h: Math.min(imgH, 1),
    })
  }

  // Date
  titleSlide.addText(formatDate(), {
    x: 0.7,
    y: 4.3,
    w: 4,
    h: 0.35,
    fontSize: 13,
    fontFace: FONT,
    color: '999999',
  })

  // Brand mark bottom-right
  titleSlide.addText(BRAND.name, {
    x: 7,
    y: 4.3,
    w: 2.5,
    h: 0.35,
    fontSize: 11,
    fontFace: FONT,
    color: BRAND.primaryColorHex,
    align: 'right',
  })

  // -------------------------------------------------------------------------
  // Summary Slide
  // -------------------------------------------------------------------------

  const summarySlide = pptx.addSlide({ masterName: 'SURVEYTIP_MASTER' })

  // Section title
  summarySlide.addText('Overview', {
    x: 0.7,
    y: 0.35,
    w: 8.6,
    h: 0.6,
    fontSize: 24,
    fontFace: FONT,
    bold: true,
    color: '333333',
  })

  // Accent underline
  summarySlide.addShape(pptx.ShapeType.rect, {
    x: 0.7,
    y: 0.95,
    w: 1.2,
    h: 0.04,
    fill: { color: BRAND.primaryColorHex },
  })

  // Stats cards — centered
  const cardWidth = 2.7
  const cardHeight = 1.9
  const cardY = 1.5
  const cardGap = 0.3
  const totalCardsW = cardWidth * 3 + cardGap * 2
  const cardStartX = (10 - totalCardsW) / 2

  const stats = [
    { label: 'Total Responses', value: String(totalResponses) },
    { label: 'Complete', value: String(completeResponses) },
    { label: 'Completion Rate', value: `${completionRate}%` },
  ]

  stats.forEach((stat, idx) => {
    const x = cardStartX + idx * (cardWidth + cardGap)

    // Card background
    summarySlide.addShape(pptx.ShapeType.roundRect, {
      x,
      y: cardY,
      w: cardWidth,
      h: cardHeight,
      fill: { color: 'FAF9F5' },
      line: { color: 'E8E5DF', pt: 1 },
      rectRadius: 0.1,
    })

    // Colored top accent inside card
    summarySlide.addShape(pptx.ShapeType.rect, {
      x: x + 0.08,
      y: cardY + 0.08,
      w: cardWidth - 0.16,
      h: 0.06,
      fill: { color: BRAND.primaryColorHex },
    })

    // Label
    summarySlide.addText(stat.label, {
      x,
      y: cardY + 0.35,
      w: cardWidth,
      h: 0.35,
      fontSize: 11,
      fontFace: FONT,
      color: '888888',
      align: 'center',
    })

    // Value
    summarySlide.addText(stat.value, {
      x,
      y: cardY + 0.7,
      w: cardWidth,
      h: 1,
      fontSize: 40,
      fontFace: FONT,
      bold: true,
      color: BRAND.primaryColorHex,
      align: 'center',
      valign: 'middle',
    })
  })

  // Survey name below cards
  const infoY = cardY + cardHeight + 0.45
  summarySlide.addText(
    [
      { text: 'Survey: ', options: { fontSize: 13, color: '888888', fontFace: FONT } },
      { text: title, options: { fontSize: 13, color: '444444', fontFace: FONT, bold: true } },
    ],
    {
      x: 0.7,
      y: infoY,
      w: 8.6,
      h: 0.35,
    }
  )

  // -------------------------------------------------------------------------
  // Chart Slides — one chart per slide, crisp canvas capture
  // -------------------------------------------------------------------------

  if (chartsContainer) {
    const chartCards = chartsContainer.querySelectorAll('.chart-card')

    for (const chartCard of chartCards) {
      const chartSlide = pptx.addSlide({ masterName: 'SURVEYTIP_MASTER' })

      // Get chart title from header
      const header = chartCard.querySelector('.chart-header')
      const chartTitle = header?.textContent || 'Chart'

      // Section title
      chartSlide.addText(chartTitle, {
        x: 0.7,
        y: 0.35,
        w: 8.6,
        h: 0.55,
        fontSize: 20,
        fontFace: FONT,
        bold: true,
        color: '333333',
      })

      // Non-Latin title image fallback (overlaid)
      if (hasNonLatinChars(chartTitle)) {
        try {
          const img = await textToImage(chartTitle, {
            fontSize: 20,
            fontWeight: 'bold',
            color: '#333333',
            maxWidth: 800,
          })
          const imgW = Math.min(8.6, img.width / 2 / 96)
          const imgH = (img.height / img.width) * imgW
          chartSlide.addImage({
            data: img.imgData,
            x: 0.7,
            y: 0.35,
            w: imgW,
            h: Math.min(imgH, 0.55),
          })
        } catch {
          /* keep text only */
        }
      }

      // Accent underline
      chartSlide.addShape(pptx.ShapeType.rect, {
        x: 0.7,
        y: 0.92,
        w: 1.2,
        h: 0.04,
        fill: { color: BRAND.primaryColorHex },
      })

      // Light frame behind chart
      const frameX = 0.45
      const frameY = 1.15
      const frameW = 9.1
      const frameH = 3.8
      chartSlide.addShape(pptx.ShapeType.roundRect, {
        x: frameX,
        y: frameY,
        w: frameW,
        h: frameH,
        fill: { color: 'FAF9F5' },
        line: { color: 'E8E5DF', pt: 0.75 },
        rectRadius: 0.08,
      })

      // Capture chart — prefer direct canvas for crisp output
      const canvasEl = chartCard.querySelector('canvas')

      try {
        let imgData
        let imgAspect

        if (canvasEl && canvasEl.width > 0 && canvasEl.height > 0) {
          imgData = canvasEl.toDataURL('image/png')
          imgAspect = canvasEl.width / canvasEl.height
        } else {
          const captured = await html2canvas(chartCard, {
            scale: 1.5,
            backgroundColor: '#ffffff',
            logging: false,
          })
          imgData = captured.toDataURL('image/jpeg', 0.85)
          imgAspect = captured.width / captured.height
        }

        // Fit within frame with padding, preserving aspect ratio
        const padX = 0.3
        const padY = 0.2
        const availW = frameW - padX * 2
        const availH = frameH - padY * 2
        const availAspect = availW / availH

        let drawW, drawH
        if (imgAspect >= availAspect) {
          drawW = availW
          drawH = availW / imgAspect
        } else {
          drawH = availH
          drawW = availH * imgAspect
        }

        // Center within frame
        const drawX = frameX + (frameW - drawW) / 2
        const drawY = frameY + (frameH - drawH) / 2

        chartSlide.addImage({ data: imgData, x: drawX, y: drawY, w: drawW, h: drawH })
      } catch (err) {
        console.error('Failed to capture chart:', err)
        chartSlide.addText('Chart could not be rendered', {
          x: 0.5,
          y: 2.5,
          w: 9,
          h: 0.5,
          fontSize: 14,
          fontFace: FONT,
          color: '999999',
          align: 'center',
        })
      }
    }
  }

  // -------------------------------------------------------------------------
  // Closing Slide
  // -------------------------------------------------------------------------

  const closingSlide = pptx.addSlide()

  // Full background
  closingSlide.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 0,
    w: '100%',
    h: '100%',
    fill: { color: BRAND.primaryColorHex },
  })

  // Decorative accent line
  closingSlide.addShape(pptx.ShapeType.rect, {
    x: 3.5,
    y: 2.0,
    w: 3,
    h: 0.04,
    fill: { color: 'FFFFFF' },
  })

  // "Thank You"
  closingSlide.addText('Thank You', {
    x: 0,
    y: 2.2,
    w: '100%',
    h: 0.9,
    fontSize: 42,
    fontFace: FONT,
    bold: true,
    color: 'FFFFFF',
    align: 'center',
  })

  // Brand name
  closingSlide.addText(BRAND.name, {
    x: 0,
    y: 3.25,
    w: '100%',
    h: 0.45,
    fontSize: 16,
    fontFace: FONT,
    color: 'FFFFFF',
    align: 'center',
  })

  // Date
  closingSlide.addText(formatDate(), {
    x: 0,
    y: 3.75,
    w: '100%',
    h: 0.35,
    fontSize: 11,
    fontFace: FONT,
    color: 'D0D4F8',
    align: 'center',
  })

  // Save the PowerPoint
  const filename = `${sanitizeFilename(title)}_Analytics_${formatDateForFilename()}.pptx`
  await pptx.writeFile({ fileName: filename })
}

// =============================================================================
// Excel Export
// =============================================================================

/**
 * Export raw response data to Excel
 *
 * @param {Object} survey - Survey data
 * @param {Array} responses - Response data array
 * @returns {void}
 */
export function exportToExcel(survey, responses) {
  const title = survey?.title || 'Survey'
  const questions = survey?.definition?.questions || []

  // Create workbook
  const wb = XLSX.utils.book_new()

  // -------------------------------------------------------------------------
  // Summary Sheet
  // -------------------------------------------------------------------------

  const summaryData = [
    ['SurveyTip - Survey Response Export'],
    [],
    ['Survey Name', title],
    ['Export Date', formatDate()],
    ['Total Responses', responses.length],
    ['Complete Responses', responses.filter((r) => r.is_complete).length],
    [],
    ['Powered by SurveyTip - https://surveytip.com'],
  ]

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)

  // Set column widths for summary
  summarySheet['!cols'] = [{ wch: 20 }, { wch: 40 }]

  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary')

  // -------------------------------------------------------------------------
  // Responses Sheet
  // -------------------------------------------------------------------------

  // Build headers
  const headers = ['Response ID', 'Submitted At', 'Is Complete', 'Duration (sec)']

  // Add question columns
  const questionIds = questions.map((q) => q.id)
  questions.forEach((q) => {
    headers.push(q.label || q.id)
  })

  // Add additional columns
  headers.push('Location', 'Timezone', 'Language', 'Device', 'Browser')

  // Build data rows
  const dataRows = responses.map((response) => {
    const row = [
      response.id,
      response.submitted_at ? new Date(response.submitted_at).toLocaleString() : '',
      response.is_complete ? 'Yes' : 'No',
      response.duration_ms ? Math.round(response.duration_ms / 1000) : '',
    ]

    // Add answers in order of questions
    questionIds.forEach((qId) => {
      const answer = response.answers?.[qId]
      if (answer === undefined || answer === null) {
        row.push('')
      } else if (Array.isArray(answer)) {
        row.push(answer.join(', '))
      } else if (typeof answer === 'object') {
        row.push(JSON.stringify(answer))
      } else if (
        typeof answer === 'number' ||
        (typeof answer === 'string' && answer !== '' && !isNaN(Number(answer)))
      ) {
        row.push(Number(answer))
      } else {
        row.push(String(answer))
      }
    })

    // Add additional data
    row.push(getLocation(response))
    row.push(response.timezone || '')
    row.push(response.language || '')
    row.push(response.client_info?.device || '')
    row.push(response.client_info?.browser || '')

    return row
  })

  // Create sheet with headers + data
  const responsesData = [headers, ...dataRows]
  const responsesSheet = XLSX.utils.aoa_to_sheet(responsesData)

  // Set column widths
  const colWidths = headers.map((h, i) => {
    if (i === 0) return { wch: 36 } // UUID
    if (h === 'Submitted At') return { wch: 20 }
    return { wch: Math.max(15, h.length + 2) }
  })
  responsesSheet['!cols'] = colWidths

  // Freeze header row
  responsesSheet['!freeze'] = { xSplit: 0, ySplit: 1 }

  XLSX.utils.book_append_sheet(wb, responsesSheet, 'Responses')

  // -------------------------------------------------------------------------
  // Questions Reference Sheet
  // -------------------------------------------------------------------------

  const questionsData = [['Question ID', 'Label', 'Type', 'Required', 'Options']]

  questions.forEach((q) => {
    const options =
      q.options?.map((o) => (typeof o === 'string' ? o : o.label || o.value)).join(', ') || ''
    questionsData.push([q.id, q.label || '', q.type || '', q.required ? 'Yes' : 'No', options])
  })

  const questionsSheet = XLSX.utils.aoa_to_sheet(questionsData)
  questionsSheet['!cols'] = [{ wch: 20 }, { wch: 40 }, { wch: 15 }, { wch: 10 }, { wch: 50 }]

  XLSX.utils.book_append_sheet(wb, questionsSheet, 'Questions')

  // Save the workbook
  const filename = `${sanitizeFilename(title)}_Responses_${formatDateForFilename()}.xlsx`
  XLSX.writeFile(wb, filename)
}

// =============================================================================
// Combined Export Function
// =============================================================================

/**
 * Export analytics in specified format
 *
 * @param {'pdf'|'ppt'|'excel'} format - Export format
 * @param {Object} options - Export options
 * @param {Object} options.survey - Survey data
 * @param {Object} options.analytics - Analytics summary (for pdf/ppt)
 * @param {Array} options.responses - Response data array
 * @param {HTMLElement} options.chartsContainer - DOM element containing charts (for pdf/ppt)
 * @returns {Promise<void>}
 */
export async function exportAnalytics(format, { survey, analytics, responses, chartsContainer }) {
  switch (format) {
    case 'pdf':
      await exportToPDF(survey, analytics, responses, chartsContainer)
      break
    case 'ppt':
      await exportToPPT(survey, analytics, responses, chartsContainer)
      break
    case 'excel':
      exportToExcel(survey, responses)
      break
    default:
      throw new Error(`Unknown export format: ${format}`)
  }
}

export default {
  exportToPDF,
  exportToPPT,
  exportToExcel,
  exportAnalytics,
}
